import type { DeviceOrientationSample } from '@citizen-walk/shared-types';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * デバイス方位センサーラッパー hook (Sprint BF Part 1、GAP-U1)。
 *
 * `window.DeviceOrientationEvent` を購読し、最新の {alpha, beta, gamma} サンプルを
 * 提供する。投稿時に `getCurrentOrientation()` を呼ぶと現在値の snapshot を返し、
 * PostForm はこれを `Post.orientation` として Firestore に保存する。
 *
 * 旧 v1 では未実装 (Phase 6+ の AR/3D 表示を見据えた foundation のみ Phase 5 で先取り)。
 *
 * iOS Safari 13+ 仕様:
 *   - `DeviceOrientationEvent.requestPermission()` が必要 (user gesture コンテキスト内)
 *   - 拒否 / unsupported の場合は `permissionState='denied'` で snapshot は常に null
 *
 * Android / Chrome:
 *   - permission request 不要、`addEventListener('deviceorientation', ...)` で即購読可能
 *
 * 失敗時の挙動: silent (warning なし、orientation=null で投稿を続行)。
 */

/** iOS 13+ 用 type narrowing (TS lib に requestPermission が無いため拡張)。 */
interface DeviceOrientationEventIOS extends DeviceOrientationEvent {
  webkitCompassHeading?: number;
}

interface DeviceOrientationEventConstructorIOS {
  requestPermission?: () => Promise<'granted' | 'denied'>;
}

type PermissionState = 'unknown' | 'unsupported' | 'prompt' | 'granted' | 'denied';

export interface UseDeviceOrientation {
  /** 最新サンプル (subscribe 中のみ更新)。 */
  sample: DeviceOrientationSample | null;
  /** 権限状態。 */
  permissionState: PermissionState;
  /** 現時点の snapshot を返す (投稿時に呼ぶ)。未取得なら null。 */
  getCurrentOrientation: () => DeviceOrientationSample | null;
  /**
   * iOS の場合 `requestPermission()` を呼んで購読開始。Android では即購読。
   * 失敗時は silent (permissionState のみ更新)。
   */
  requestPermissionAndStart: () => Promise<void>;
}

function isDeviceOrientationSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.DeviceOrientationEvent !== 'undefined';
}

function needsIosPermission(): boolean {
  if (!isDeviceOrientationSupported()) return false;
  const ctor = window.DeviceOrientationEvent as unknown as DeviceOrientationEventConstructorIOS;
  return typeof ctor.requestPermission === 'function';
}

export function useDeviceOrientation(): UseDeviceOrientation {
  const [sample, setSample] = useState<DeviceOrientationSample | null>(null);
  const [permissionState, setPermissionState] = useState<PermissionState>(() => {
    if (!isDeviceOrientationSupported()) return 'unsupported';
    return needsIosPermission() ? 'prompt' : 'unknown';
  });
  const sampleRef = useRef<DeviceOrientationSample | null>(null);
  const listenerAttachedRef = useRef(false);

  const handleEvent = useCallback((event: DeviceOrientationEvent) => {
    const ev = event as DeviceOrientationEventIOS;
    // iOS は webkitCompassHeading が compass-true 補正値 (0..360, 北=0)。
    // alpha と方向 sign 反転のため、利用可能なら優先採用 (旧 v1 想定設計と同 pattern)。
    const alpha =
      typeof ev.webkitCompassHeading === 'number'
        ? ev.webkitCompassHeading
        : typeof ev.alpha === 'number'
          ? ev.alpha
          : null;
    const next: DeviceOrientationSample = {
      alpha,
      beta: typeof ev.beta === 'number' ? ev.beta : null,
      gamma: typeof ev.gamma === 'number' ? ev.gamma : null,
      capturedAt: new Date().toISOString(),
    };
    sampleRef.current = next;
    setSample(next);
  }, []);

  const attachListener = useCallback(() => {
    if (listenerAttachedRef.current) return;
    if (typeof window === 'undefined') return;
    window.addEventListener('deviceorientation', handleEvent, true);
    listenerAttachedRef.current = true;
  }, [handleEvent]);

  // Android (permission 不要) は mount 時に自動 attach。iOS は requestPermissionAndStart 待ち。
  useEffect(() => {
    if (!isDeviceOrientationSupported()) return;
    if (needsIosPermission()) return; // iOS は user gesture 待ち
    attachListener();
    setPermissionState('granted');
    return () => {
      if (listenerAttachedRef.current && typeof window !== 'undefined') {
        window.removeEventListener('deviceorientation', handleEvent, true);
        listenerAttachedRef.current = false;
      }
    };
  }, [attachListener, handleEvent]);

  const requestPermissionAndStart = useCallback(async () => {
    if (!isDeviceOrientationSupported()) {
      setPermissionState('unsupported');
      return;
    }
    if (!needsIosPermission()) {
      attachListener();
      setPermissionState('granted');
      return;
    }
    try {
      const ctor = window.DeviceOrientationEvent as unknown as DeviceOrientationEventConstructorIOS;
      const result = await ctor.requestPermission?.();
      if (result === 'granted') {
        attachListener();
        setPermissionState('granted');
      } else {
        setPermissionState('denied');
      }
    } catch {
      // user gesture 外 / その他失敗は silent
      setPermissionState('denied');
    }
  }, [attachListener]);

  const getCurrentOrientation = useCallback((): DeviceOrientationSample | null => {
    return sampleRef.current;
  }, []);

  return { sample, permissionState, getCurrentOrientation, requestPermissionAndStart };
}
